const PORTAL_API_BASE = '/api/portal';

interface PortalApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code?: string;
    message: string;
  };
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

function getAccessToken(): string | null {
  return localStorage.getItem('crm_portal_access_token');
}

function getRefreshToken(): string | null {
  return localStorage.getItem('crm_portal_refresh_token');
}

function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem('crm_portal_access_token', accessToken);
  localStorage.setItem('crm_portal_refresh_token', refreshToken);
}

function clearTokens(): void {
  localStorage.removeItem('crm_portal_access_token');
  localStorage.removeItem('crm_portal_refresh_token');
}

/**
 * Attempt to refresh the portal access token using the stored refresh token.
 * Returns true if refresh succeeded.
 */
async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  // If already refreshing, wait for the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${PORTAL_API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const result: PortalApiResponse<{ accessToken: string; refreshToken: string }> =
        await response.json();

      if (result.success && result.data) {
        setTokens(result.data.accessToken, result.data.refreshToken);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Portal-specific logout: clear tokens and redirect to portal login.
 */
function portalLogout(): void {
  clearTokens();
  window.location.href = '/portal/login';
}

/**
 * Portal API fetch wrapper that:
 * 1. Adds the portal access token as Authorization header
 * 2. On 401, attempts token refresh and retries the original request
 * 3. On failed refresh, logs the user out
 */
export async function portalFetch(
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = getAccessToken();

  const headers: HeadersInit = {
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(endpoint, { ...options, headers });

  // If not 401, return as-is
  if (response.status !== 401) {
    return response;
  }

  // Attempt token refresh
  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    portalLogout();
    throw new Error('Session expired. Please log in again.');
  }

  // Retry with new token
  const newToken = getAccessToken();
  (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;

  return fetch(endpoint, { ...options, headers });
}

/**
 * Convenience wrapper for GET requests that parses JSON and checks success.
 */
export async function portalGet<T>(endpoint: string): Promise<T> {
  const res = await portalFetch(`${PORTAL_API_BASE}${endpoint}`);
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error?.message || `Request failed (${res.status})`);
  }
  const data: PortalApiResponse<T> = await res.json();
  if (!data.success) {
    throw new Error(data.error?.message || 'Request failed');
  }
  return data.data as T;
}

/**
 * Convenience wrapper for POST requests that parses JSON and checks success.
 */
export async function portalPost<T>(
  endpoint: string,
  body?: unknown,
): Promise<T> {
  const res = await portalFetch(`${PORTAL_API_BASE}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error?.message || `Request failed (${res.status})`);
  }
  const data: PortalApiResponse<T> = await res.json();
  if (!data.success) {
    throw new Error(data.error?.message || 'Request failed');
  }
  return data.data as T;
}
